import React, { useEffect, useState } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  updateWidgetProperty,
  toggleEditMode,
  updateEditorAppearance,
  actionGroup,
} from "actions/editor";

import _ from "lodash";
import cx from "classnames";
import { Frame } from "scenejs";

import { EditorContext } from "../../index";
import StoryboardContext from "components/Storyboard/context";
import { Container } from "../core/container";
import { GlassThemesSettings } from "../slideRender/components";
import { getBoundingRectFromLayout } from "components/Editor/helper/layoutManager";
import {
  useChildrenWidgetSelected,
  useEmptyWidgetSelection,
  useHotspotWidgetSelector,
  useWidgetSelector,
  useWidgetChildrenSelector,
} from "components/Editor/hooks/useEditorSession";

import { Widget } from "./../../widgets/core";
import { SlidesContext } from "../slideRender";
import { Button } from "react-bootstrap";

import { MediaLibraryModal } from "components/MediaLibrary";
import { DEFAULT_MEDIA_PROPERTY } from "constants/background";
import Portal from "components/Portal";

import { Property } from "../../components/properties/";

import store from "store";
import { SLIDE } from "constants/widgetTypes";
import { STORYBOARD_TYPES } from "constants/storyboardType";

import styles from "./slide.module.scss";

// Minimum height for a resizable flipbook slide (matches the slide onResize floor).
const MIN_SLIDE_HEIGHT = 600;

// All visible SLIDE widgets in the editor session, ordered the same way the
// slide renderer orders them (`order` asc). The first entry is the first slide.
const getOrderedFlipbookSlides = (widgets) =>
  _.orderBy(
    (widgets || []).filter(
      (w) => w.type === SLIDE && _.get(w, "properties.desktop.visible")
    ),
    "order",
    "asc"
  );

// Flipbook slides are resizable in mobile view for every slide except the first.
const isResizableFlipbookSlide = (firstSlideUid, widgetUid, storyboardType, breakpoint) =>
  storyboardType === STORYBOARD_TYPES.FLIP_BOOK &&
  breakpoint === "mobile" &&
  !!firstSlideUid &&
  firstSlideUid !== widgetUid;

// Bottom drag handle for a flipbook slide — mirrors the page/"frame" PageResizeHandle.
// On drag it writes the slide card height into the reducer via updateWidgetProperty.
const SlideResizeHandle = ({ editor, slideId, breakpoint }) => {
  const dispatch = useDispatch();
  const dragStateRef = React.useRef(null);
  const [show, setShow] = useState(false);

  const slide = useWidgetSelector(editor, slideId);
  const widgetChildren = useWidgetChildrenSelector(editor, slideId) || [];
  const height =
    parseInt(_.get(slide, `properties.${breakpoint}.layout.height`)) || 0;

  const onHeightChange = (newHeight) => {
    const layout = _.get(slide, `properties.${breakpoint}.layout`) || {};
    dispatch(
      updateWidgetProperty(editor, slideId, breakpoint, {
        layout: {
          ...layout,
          height: `${newHeight}px`,
        },
      })
    );
  };

  // Show the handle when hovering near the bottom edge. Listen on the slide
  // wrapper (not the slide element) so moving onto the overlapping "Add Slide"
  // strip — a sibling that sits above the slide — does not count as leaving.
  useEffect(() => {
    const wrapper = document.getElementsByClassName("slideRender_Wrapper")[0];
    if (!wrapper) return;

    const hoverZone = 100;
    const handleMouseMove = (e) => {
      const rect = wrapper.getBoundingClientRect();
      const y = e.clientY - rect.top;
      setShow(y >= rect.height - hoverZone);
    };
    const handleMouseLeave = () => setShow(false);

    wrapper.addEventListener("mousemove", handleMouseMove);
    wrapper.addEventListener("mouseleave", handleMouseLeave);
    return () => {
      wrapper.removeEventListener("mousemove", handleMouseMove);
      wrapper.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [slideId]);

  const handleBottomResizeStart = (e) => {
    e.stopPropagation();
    e.preventDefault();

    // The slide cannot shrink past the bottom of its visible content.
    let boundary = 0;
    widgetChildren.forEach((child) => {
      const transform = _.get(
        child,
        `properties.${breakpoint}.layout.transform.translate`,
        ""
      );
      const visible = _.get(child, `properties.${breakpoint}.visible`, true);
      if (!visible || !transform) return;

      const parts = transform.split(",");
      const y = parseFloat(parts[1]?.replace("px", "").trim()) || 0;
      const element = document.getElementById(child?.uid);
      if (element) {
        const rect = element.getBoundingClientRect();
        boundary = Math.max(boundary, y + rect.height);
      }
    });

    // The card frame is the slide wrapper; resize it live (transition muted so
    // dragging tracks the cursor), and persist the height into the reducer.
    const wrapper = document.getElementsByClassName("slideRender_Wrapper")[0];
    const prevTransition = wrapper ? wrapper.style.transition : "";
    if (wrapper) wrapper.style.transition = "none";

    actionGroup.start();

    dragStateRef.current = {
      startY: e.clientY,
      startHeight: height,
      onMouseMove: (moveEvent) => {
        if (!dragStateRef.current) return;

        const deltaY = moveEvent.clientY - dragStateRef.current.startY;
        let newHeight = dragStateRef.current.startHeight + deltaY;
        newHeight = Math.max(MIN_SLIDE_HEIGHT, newHeight);
        if (boundary > 0 && newHeight < boundary) {
          newHeight = boundary;
        }

        const el = document.getElementById(slideId);
        if (el) {
          el.style.height = `${newHeight}px`;
        }
        if (wrapper) {
          wrapper.style.height = `${newHeight}px`;
        }
        onHeightChange(newHeight);
      },
      onMouseUp: () => {
        if (!dragStateRef.current) return;

        const el = document.getElementById(slideId);
        if (el) {
          let finalHeight = parseInt(el.style.height, 10) || 0;
          if (boundary > 0 && finalHeight < boundary) {
            finalHeight = boundary;
            el.style.height = `${finalHeight}px`;
            if (wrapper) wrapper.style.height = `${finalHeight}px`;
            onHeightChange(finalHeight);
          }
        }

        if (wrapper) wrapper.style.transition = prevTransition;

        document.removeEventListener("mousemove", dragStateRef.current.onMouseMove);
        document.removeEventListener("mouseup", dragStateRef.current.onMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        dragStateRef.current = null;
        actionGroup.end();
      },
    };

    document.addEventListener("mousemove", dragStateRef.current.onMouseMove);
    document.addEventListener("mouseup", dragStateRef.current.onMouseUp);
    document.body.style.userSelect = "none";
    document.body.style.cursor = "ns-resize";
  };

  // Cleanup any in-flight drag on unmount.
  useEffect(() => {
    return () => {
      if (dragStateRef.current) {
        document.removeEventListener("mousemove", dragStateRef.current.onMouseMove);
        document.removeEventListener("mouseup", dragStateRef.current.onMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";
      }
    };
  }, []);

  // Mount inside the slide wrapper (sibling to the "Add Slide" strip) so the
  // button shares its stacking context and can sit above it via z-index.
  const mountNode = document.getElementsByClassName("slideRender_Wrapper")[0];
  if (!mountNode) return null;

  return (
    <Portal mountNode={mountNode}>
      <Button
        id="slide-resize-button"
        name="slide-resize-button"
        onMouseDown={handleBottomResizeStart}
        size="sm"
        variant="primary"
        className={cx(styles.resizeButton, {
          [styles.hovering]: show,
        })}
      >
        <i className={cx(styles.icon, "cleverstory-stretch")} />
      </Button>
    </Portal>
  );
};

const SlideWidgetComponent = function ({
  className,
  style,
  widgetProps,
  widgetRef,
  children,
}) {
  const ref = widgetRef;

  const dispatch = useDispatch();

  const editorContext = React.useContext(EditorContext);
  const editor = editorContext.editorSession.id;
  const slideRenderViewer = document.getElementsByClassName(
    "slideRender_Wrapper"
  )[0];

  const breakpoint = useSelector((state) =>
    _.get(state, `${editor}.present.breakpoint`)
  );

  const id = _.get(widgetProps, "uid");
  const type = _.get(widgetProps, "type");

  const storyboard = React.useContext(StoryboardContext);
  const storyboardType = _.get(storyboard, 'type');

  const visibleWitdh = _.get(widgetProps, `properties.${breakpoint}.container.minWidth`) || 1200;

  const widgetHeight = parseInt(_.get(widgetProps, `properties.${breakpoint}.layout.height`)) ||  675;
  const hotspotWidget = useHotspotWidgetSelector(editor, breakpoint);
  const hotspotWidgetParent = useWidgetSelector(editor, hotspotWidget && hotspotWidget?.parent);
  const hotspotVisibility = _.get(hotspotWidgetParent, `properties.${breakpoint}.visible`)
  const [height, setHeight] = useState(`${widgetHeight}px`);

  const showToolbars = useEmptyWidgetSelection(editor);

  // uid of the first slide (order asc) — used to keep the first slide fixed.
  const firstSlideUid = useSelector((state) =>
    _.get(
      getOrderedFlipbookSlides(_.get(state, `${editor}.present.widgets`, [])),
      "[0].uid"
    )
  );
  const canResizeSlide = isResizableFlipbookSlide(
    firstSlideUid,
    id,
    storyboardType,
    breakpoint
  );

  React.useEffect(() => {
    // Resizable flipbook slides own their height via the resize handle, so skip
    // the height-pinning sync that keeps other slides at their fixed height.
    if (canResizeSlide) return;

    const layout = _.get(widgetProps, `properties.${breakpoint}.layout`) || {};
    if (layout && layout.height != height) {
      dispatch(
        updateWidgetProperty(editor, id, breakpoint, {
          layout: {
            ...layout,
            height: height,
          },
        })
      );
    }
  }, [height, children, canResizeSlide]);

  // Measure the bottom of the slide's visible content, relative to the slide top.
  const measureContentHeight = () => {
    const slideEl = document.getElementById(id);
    if (!slideEl) return 0;

    const parentTop = slideEl.getBoundingClientRect().top;
    let contentHeight = 0;
    slideEl.querySelectorAll(".widget[data-widget-id]").forEach((el) => {
      const rect = el.getBoundingClientRect();
      contentHeight = Math.max(contentHeight, rect.bottom - parentTop);
    });
    return contentHeight;
  };

  // On switching to mobile, auto-fit a resizable subslide's card height to its
  // content (min 600) so it isn't stuck at the fixed desktop/default height.
  React.useEffect(() => {
    if (!canResizeSlide) return;
    if (breakpoint !== "mobile") return;

    const raf = requestAnimationFrame(() => {
      const newHeight = Math.max(600, Math.ceil(measureContentHeight()));
      const layout = _.get(widgetProps, `properties.${breakpoint}.layout`) || {};
      const currentHeight = parseInt(layout.height) || 0;

      if (newHeight && newHeight !== currentHeight) {
        dispatch(
          updateWidgetProperty(editor, id, breakpoint, {
            layout: {
              ...layout,
              height: `${newHeight}px`,
            },
          })
        );
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [breakpoint, canResizeSlide]);

  var widgetStyle = {
    ...style,
    width: breakpoint === "desktop" ? `${visibleWitdh}px` : `${visibleWitdh}px`,
    minHeight: breakpoint === "desktop" ? `675px` : `666.67px`,
  };

  return (
    <Container widgetProps={widgetProps} widgetRef={widgetRef}>
      <div
        id={id}
        ref={ref}
        className={cx(className, styles.slideSection, "cs_slide_root", {
          [styles.mobile]: breakpoint === "mobile",
        })}
        data-widget-id={id}
        data-widget-type={type}
        data-container={true}
        data-selectable={false}
        data-storyboard-type={storyboardType}
        style={widgetStyle}
      >
        { 
          children
        }

        {( hotspotWidget && hotspotVisibility ) && (
          <Widget key={hotspotWidget.uid} widgetProps={hotspotWidget} />
        )}

        {canResizeSlide && showToolbars && (
          <SlideResizeHandle
            editor={editor}
            slideId={id}
            breakpoint={breakpoint}
          />
        )}

      </div>
    </Container>
  );
};

const slideWidgetEquals = function (prevProps, nextProps) {
  const paths = ["className", "style", "widgetProps", "widgetRef", "children"];
  const next = _.pick(nextProps, paths);
  const previous = _.pick(prevProps, paths);

  const equals = _.isEqual(next, previous);
  return equals;
};

const SlideWidget = React.memo(SlideWidgetComponent, slideWidgetEquals);


SlideWidget.moveableConfig = (editorSession, widget, breakpoint) => {
  const element = document.querySelector("[data-storyboard-type]");
  const storyboardType = element?.getAttribute("data-storyboard-type");
  const slides = getOrderedFlipbookSlides(
    _.get(store.getState(), `${editorSession}.present.widgets`, [])
  );
  const canResize = isResizableFlipbookSlide(
    _.get(slides, "[0].uid"),
    _.get(widget, "uid"),
    storyboardType,
    breakpoint
  );

  return {
    draggable: false,
    resizable: canResize,
    rotatable: false,
    selectable: false,
    cloneable: false,
    deletable: false,
    renderDirections: canResize ? ["s"] : [],
    onResize: (widget, layout) => {
      const frame = new Frame(layout);

      if (frame.has("transform", "translate")) {
        frame.set("transform", "translate", "0px, 0px");
      }

      const rect = getBoundingRectFromLayout(layout);
      const height = Math.max(600, rect.height);

      frame.set("height", `${height}px`);

      return frame.get();
    },
    onReparent: (widget, parent) => {
      return false;
    },
  };
};

SlideWidget.processRequest = (widget) => {
  var properties = widget.properties;

  var background = _.get(properties, "desktop.background");
  var media = _.get(background, "media");
  if (background && media) {
    properties = {
      ...properties,
      desktop: {
        ...properties.desktop,
        background: {
          ...background,
          media: media.id,
        },
      },
    };
  }

  return {
    ...widget,
    properties: properties,
  };
};

export { SlideWidget };
